import type { BookingPromotionRedemption } from "../../../lib/admin-console-api";
import type { PromotionRedemptionRow } from "./types";

/**
 * Adapt a live server redemption record to the presentational row.
 * No fields are invented: amounts pass through as decimal strings,
 * timestamps pass through as ISO instants, and missing finals stay null
 * (shown as "Unknown", never 0).
 */
export function toPromotionRedemptionRow(record: BookingPromotionRedemption): PromotionRedemptionRow {
  return {
    booking_id: record.booking_id,
    status: record.status,
    reserved_credit_amount: record.reserved_credit_amount,
    final_credit_amount: record.final_credit_amount,
    reserved_at: record.reserved_at,
    consumed_at: record.consumed_at,
    released_at: record.released_at,
    venue_id: record.venue_id,
    revision_id: record.revision_id,
    reserved_gross_commission_amount: record.reserved_gross_commission_amount,
    final_gross_commission_amount: record.final_gross_commission_amount,
  };
}

/** Final credit once known, else null (Unknown) — never a measured 0, never held credit. */
export function redemptionFinalCredit(row: PromotionRedemptionRow): string | null {
  return row.final_credit_amount;
}

/** When the hold was placed. Always present on live rows. */
export function redemptionReservedAt(row: PromotionRedemptionRow): string | null {
  return row.reserved_at;
}

/**
 * The closing instant matching the row status: consumed_at for consumed,
 * released_at for released, otherwise null (still held — no closing time).
 */
export function redemptionClosedAt(row: PromotionRedemptionRow): string | null {
  if (row.status === "consumed") return row.consumed_at ?? null;
  if (row.status === "released") return row.released_at ?? null;
  return null;
}

/**
 * Frozen historical gross commission BEFORE booking savings for this
 * redemption: the reserved estimate until the booking completes, then the
 * finalized amount. This is the funding basis, never current earnings —
 * refunds and later adjustments live in aggregate metrics and are never
 * netted into this cell. Null means not measured (Unknown) — never 0.
 * Customer surfaces must never receive this.
 */
export function redemptionGrossCommission(row: PromotionRedemptionRow): {
  value: string | null;
  basis: "final" | "reserved" | null;
} {
  if (row.final_gross_commission_amount !== undefined && row.final_gross_commission_amount !== null) {
    return { basis: "final", value: row.final_gross_commission_amount };
  }
  if (row.reserved_gross_commission_amount !== undefined && row.reserved_gross_commission_amount !== null) {
    return { basis: "reserved", value: row.reserved_gross_commission_amount };
  }
  return { basis: null, value: null };
}
